import React, { PureComponent, ReactNode } from "react";
import MusicNote from "../music_visuals/history_monitor/MusicNote";
import ThreeOptionSlider from "../theme_reuse/ThreeOptionSlider";

interface Props {}
interface State {
  demoSliderOption: 0 | 1 | 2 | 3;
}

export default class NoteOverrideDemo extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      demoSliderOption: 2, // the most useful one
    };
  }

  handleDemoSliderChange = (_id: string, demoSliderOption: 0 | 1 | 2 | 3) => {
    this.setState({ demoSliderOption });
  };

  renderSliderExplanation = () => {
    switch (this.state.demoSliderOption) {
      case 0:
        return (
          <>
            <h4>3 Options</h4>
            <p>Unchecking a box restores default detection behavior for that note.</p>
            <p>
              Automated pins may auto-adjust when the key changes, but your manual pins will always be respected. So it's a good idea to
              remove your manual pins (or refresh the page) before switching songs.
            </p>
          </>
        );
      case 1:
        return (
          <>
            <h4>Option 1: Force Inclusion</h4>
            <p>Force the program to include the selected note in any valid scales returned.</p>
            <p>Usually, notes are automatically detected and thus determine the possible matching scales.</p>
            <p>Caution: If you force-enable a strange set of notes, you may rule out all possible scales.</p>
          </>
        );
      case 2:
        return (
          <>
            <h4>Option 2: Force Exclusion</h4>
            <p>Any occurrences of the selected note are completely ignored. </p>
            <p>
              This is useful when scales or chords come out wrong because of loud overtones, passing notes, or if the music isn't pitch
              perfect in{" "}
              <a href="https://en.wikipedia.org/wiki/Equal_temperament" target="_blank" rel="noreferrer">
                equal temperament
              </a>
              .
            </p>
          </>
        );
      case 3:
        return (
          <>
            <h4>Option 3: Mark an Accidental</h4>
            <p>This has the same effect as option 2.</p>
            <p>With option 3 the only difference is the note appears in note history (in yellow) if it occurs.</p>
            <p>
              Ideally accidentals would be reflected in the chords, but frankly jazz is too complicated for this simple noodler. Hopefully
              seeing the notes will help in other cases. Hey, at least it's free.
            </p>
          </>
        );
    }
  };

  render(): ReactNode {
    return (
      <>
        <h3>Manually Overriding Notes In the Scale</h3>
        <p>
          {/* When you click the {inlineIcon(ICON_NOTEOVERRIDE)} button above the 12 notes "A A# B B# C..." , it reveals 3 boxes for each note. */}
          The three boxes alongside the 12 notes "A A# B B# C..." are buttons as well as indicators.
        </p>
        <p>Underlining beneath boxes indicates that pinning is active, whether set by automation or manually.</p>
        <p>If you click on them, your choices will override the automation for each note.</p>
        <p>Click each box below to learn what they do:</p>
        <div className="help-demo-centered-flex-row">
          <MusicNote note_id="C" />
          <ThreeOptionSlider
            checkedOpt={this.state.demoSliderOption}
            onChangeOption={this.handleDemoSliderChange}
            sliderId="demo-slider"
            isPin={this.state.demoSliderOption !== 0}
            isPinManual={this.state.demoSliderOption !== 0}
          />
        </div>
        {this.renderSliderExplanation()}
      </>
    );
  }
}
